// pages/dashboard/FormMaker.jsx
// Two tabs: Assessment Builder and Feedback Form Builder
// Card based UI with up/down arrows for question ordering
// No drag and drop needed - arrows are cleaner for Steve

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

// Question type options for feedback forms
const FEEDBACK_QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple Choice', icon: '⚪', desc: 'Single selection from options' },
  { value: 'checkbox', label: 'Checkboxes', icon: '☑️', desc: 'Multiple selections allowed' },
  { value: 'short_text', label: 'Short Text', icon: '✏️', desc: 'One line text answer' },
  { value: 'long_text', label: 'Long Text', icon: '📝', desc: 'Paragraph text answer' },
  { value: 'rating', label: 'Rating Scale', icon: '⭐', desc: 'Numeric scale (e.g. 1 to 5)' },
  { value: 'nps', label: 'NPS Score', icon: '📊', desc: '0 to 10 recommendation score' },
  { value: 'yes_no', label: 'Yes / No', icon: '✅', desc: 'Simple yes or no' },
  { value: 'dropdown', label: 'Dropdown', icon: '▼', desc: 'Select from a dropdown list' },
  { value: 'ranking', label: 'Ranking', icon: '🔢', desc: 'Rank items in order' },
  { value: 'bipolar', label: 'Bipolar Scale', icon: '↔️', desc: 'Scale between two opposite labels' },
];

const Sidebar = ({ navigate, logout }) => {
  const { admin } = useAuth();
  const handleLogout = async () => { await logout(); navigate('/admin/login'); };
  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋' },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢' },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝', active: true },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];
  return (
    <div style={s.sidebar}>
      <div style={s.sidebarLogo}>
        <span style={s.logoExec}>EXECUTIVE</span>
        <span style={s.logoSpring}>Springboard</span>
      </div>
      <nav style={s.nav}>
        {navItems.map(item => (
          <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
            <span>{item.icon}</span><span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div style={s.sidebarFooter}>
        <div style={s.adminName}>{admin?.name}</div>
        <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
      </div>
    </div>
  );
};

// Clipboard helper - works on both HTTP and HTTPS
const copyToClipboard = (text, onSuccess, onFail) => {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).then(onSuccess).catch(onFail);
  } else {
    // Fallback for HTTP
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      onSuccess();
    } catch (err) {
      onFail(err);
    }
    document.body.removeChild(textarea);
  }
};

const FormMaker = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('assessment'); // assessment or feedback
  const [listView, setListView] = useState('forms'); // forms, archived or trash
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list or editor
  const [currentForm, setCurrentForm] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saving, setSaving] = useState(false);

  // New form modal state
  const [showNewForm, setShowNewForm] = useState(false);
  const [newFormData, setNewFormData] = useState({ name: '', description: '', formType: 'assessment', targetAudience: 'both' });

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/forms?view=all');
      if (response.data.success) setForms(response.data.data.forms);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async (e) => {
    e.preventDefault();
    if (!newFormData.name.trim()) return;
    setSaving(true);
    try {
      const response = await axios.post('/forms', newFormData);
      if (response.data.success) {
        setCurrentForm(response.data.data.form);
        setShowNewForm(false);
        setView('editor');
        fetchForms();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to create form' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditor = async (form) => {
    try {
      const response = await axios.get(`/forms/${form._id}`);
      if (response.data.success) {
        setCurrentForm(response.data.data.form);
        setView('editor');
        setActiveTab(response.data.data.form.formType === 'feedback' ? 'feedback' : 'assessment');
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not open form' });
    }
  };

  const handleDuplicate = async (formId) => {
    try {
      await axios.post(`/forms/${formId}/duplicate`);
      setMessage({ type: 'success', text: 'Form duplicated as draft' });
      fetchForms();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to duplicate' });
    }
  };

  const handleArchive = async (formId) => {
    if (!window.confirm('Archive this form?\n\nIt will move to the Archived view where you can restore it anytime.')) return;
    try {
      await axios.delete(`/forms/${formId}`);
      setMessage({ type: 'success', text: 'Form archived. Find it in the Archived view.' });
      fetchForms();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to archive' });
    }
  };

  const handleRestore = async (formId) => {
    try {
      const response = await axios.put(`/forms/${formId}/restore`);
      setMessage({ type: 'success', text: response.data?.message || 'Form restored' });
      fetchForms();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to restore' });
    }
  };

  const handleTrash = async (formId) => {
    if (!window.confirm('Move this form to Trash?\n\nIt will stop working everywhere. You can still restore it from Trash, or delete it forever from there.')) return;
    try {
      await axios.put(`/forms/${formId}/trash`);
      setMessage({ type: 'success', text: 'Form moved to Trash' });
      fetchForms();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to move to trash' });
    }
  };

  const handlePermanentDelete = async (form) => {
    if (form.totalSubmissions > 0) {
      setMessage({ type: 'error', text: `Cannot delete forever: this form has ${form.totalSubmissions} submission(s). Keep it archived instead.` });
      return;
    }
    if (!window.confirm(`DELETE FOREVER: "${form.name}"\n\nThis CANNOT be undone. The form and all its questions will be permanently removed.\n\nAre you absolutely sure?`)) return;
    try {
      await axios.delete(`/forms/${form._id}/permanent`);
      setMessage({ type: 'success', text: 'Form permanently deleted' });
      fetchForms();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete' });
    }
  };

  const matchesView = (f) => {
    if (listView === 'archived') return f.status === 'archived';
    if (listView === 'trash') return f.status === 'trashed';
    return f.status !== 'archived' && f.status !== 'trashed';
  };
  const assessmentForms = forms.filter(f => f.formType !== 'feedback' && matchesView(f));
  const feedbackForms = forms.filter(f => f.formType === 'feedback' && matchesView(f));
  const displayForms = activeTab === 'assessment' ? assessmentForms : feedbackForms;

  const tabForms = forms.filter(f => (activeTab === 'assessment' ? f.formType !== 'feedback' : f.formType === 'feedback'));
  const viewCounts = {
    forms: tabForms.filter(f => f.status !== 'archived' && f.status !== 'trashed').length,
    archived: tabForms.filter(f => f.status === 'archived').length,
    trash: tabForms.filter(f => f.status === 'trashed').length,
  };

  // The schema stores 'both' / 'individual' / 'enterprise'. Nobody outside the
  // codebase calls it "both", so translate at the boundary.
  const audienceLabel = (a) => (
    { both: 'Everyone', individual: 'Individuals', enterprise: 'Companies' }[a] || a || 'Everyone'
  );

  const getStatusBadge = (status) => {
    const map = {
      active: { bg: '#E8F8EF', color: '#1E8449', label: 'Active' },
      draft: { bg: '#FEF9E7', color: '#A04000', label: 'Draft' },
      inactive: { bg: '#F4F6F9', color: '#7F8C8D', label: 'Inactive' },
      archived: { bg: '#FDEDEC', color: '#943126', label: 'Archived' },
      trashed: { bg: '#FADBD8', color: '#922B21', label: 'In Trash' },
    };
    const b = map[status] || { bg: '#F4F6F9', color: '#7F8C8D', label: status };
    return <span style={{ backgroundColor: b.bg, color: b.color, padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: '500' }}>{b.label}</span>;
  };

  if (view === 'editor' && currentForm) {
    return <FormEditor form={currentForm} onBack={() => { setView('list'); setCurrentForm(null); fetchForms(); }} navigate={navigate} logout={logout} />;
  }

  return (
    <div style={s.page}>
      <Sidebar navigate={navigate} logout={logout} />

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Form Maker</h1>
            <p style={s.subtitle}>Create and manage assessment and feedback forms</p>
          </div>
          <button style={s.btnPrimary} onClick={() => setShowNewForm(true)}>
            + Create New Form
          </button>
        </div>

        {message.text && (
          <div style={{ ...s.messageBox, ...(message.type === 'success' ? s.messageSuccess : s.messageError) }}>
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div style={s.tabs}>
          <button
            style={{ ...s.tab, ...(activeTab === 'assessment' ? s.tabActive : {}) }}
            onClick={() => setActiveTab('assessment')}
          >
            📋 Assessment Forms ({assessmentForms.length})
          </button>
          <button
            style={{ ...s.tab, ...(activeTab === 'feedback' ? s.tabActive : {}) }}
            onClick={() => setActiveTab('feedback')}
          >
            💬 Feedback Forms ({feedbackForms.length})
          </button>
        </div>

        {/* Tab description */}
        <div style={s.tabDesc}>
          {activeTab === 'assessment' ? (
            <p>Assessment forms use dimensions and poles with Likert scoring. They generate AI powered leadership reports.</p>
          ) : (
            <p>Feedback forms collect general responses with various question types. Responses are stored and viewable in a table. No scoring or reports.</p>
          )}
        </div>

        {/* View switcher: Forms | Archived | Trash */}
        <div style={s.viewPills}>
          {[
            { key: 'forms', label: `Forms (${viewCounts.forms})` },
            { key: 'archived', label: `🗄 Archived (${viewCounts.archived})` },
            { key: 'trash', label: `🗑 Trash (${viewCounts.trash})` },
          ].map(v => (
            <button
              key={v.key}
              style={{ ...s.viewPill, ...(listView === v.key ? s.viewPillActive : {}) }}
              onClick={() => setListView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>

        {listView === 'archived' && (
          <div style={s.viewNote}>Archived forms are switched off but safe. Restore brings a form back exactly as it was. Delete moves it to Trash.</div>
        )}
        {listView === 'trash' && (
          <div style={s.viewNote}>Forms in Trash can be restored, or deleted forever. Delete Forever cannot be undone and is blocked while a form has submissions.</div>
        )}

        {/* New Form Modal */}
        {showNewForm && (
          <div style={s.modalOverlay} onClick={() => setShowNewForm(false)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <h2 style={s.modalTitle}>Create New Form</h2>
              <form onSubmit={handleCreateForm}>
                <div style={s.formGroup}>
                  <label style={s.label}>Form Type *</label>
                  <div style={s.typeSelector}>
                    <button
                      type="button"
                      style={{ ...s.typeOption, ...(newFormData.formType === 'assessment' ? s.typeOptionActive : {}) }}
                      onClick={() => setNewFormData(p => ({ ...p, formType: 'assessment' }))}
                    >
                      <div style={s.typeOptionIcon}>📋</div>
                      <div style={s.typeOptionLabel}>Assessment Form</div>
                      <div style={s.typeOptionDesc}>Dimensions, poles, scoring, AI report</div>
                    </button>
                    <button
                      type="button"
                      style={{ ...s.typeOption, ...(newFormData.formType === 'feedback' ? s.typeOptionActive : {}) }}
                      onClick={() => setNewFormData(p => ({ ...p, formType: 'feedback' }))}
                    >
                      <div style={s.typeOptionIcon}>💬</div>
                      <div style={s.typeOptionLabel}>Feedback Form</div>
                      <div style={s.typeOptionDesc}>Multiple question types, collect responses</div>
                    </button>
                  </div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Form Name *</label>
                  <input style={s.input} type="text" placeholder="e.g. Mentee Feedback Form" value={newFormData.name} onChange={e => setNewFormData(p => ({ ...p, name: e.target.value }))} required autoFocus />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Description</label>
                  <textarea style={s.textarea} rows={2} placeholder="Brief description..." value={newFormData.description} onChange={e => setNewFormData(p => ({ ...p, description: e.target.value }))} />
                </div>
                <div style={s.modalActions}>
                  <button type="button" style={s.btnCancel} onClick={() => setShowNewForm(false)}>Cancel</button>
                  <button type="submit" style={s.btnCreate} disabled={saving}>
                    {saving ? 'Creating...' : 'Create Form'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Forms List */}
        {loading ? (
          <p style={s.loadingText}>Loading forms...</p>
        ) : displayForms.length === 0 ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>{listView === 'trash' ? '🗑' : listView === 'archived' ? '🗄' : (activeTab === 'assessment' ? '📋' : '💬')}</div>
            <p style={s.emptyTitle}>
              {listView === 'archived' ? `No archived ${activeTab} forms` : listView === 'trash' ? `Trash is empty` : `No ${activeTab} forms yet`}
            </p>
            {listView === 'forms' && (
              <>
                <p style={s.emptyText}>
                  {activeTab === 'assessment'
                    ? 'Create an assessment form with dimensions, poles and questions. The system will generate AI reports from responses.'
                    : 'Create a feedback form to collect responses from clients or mentees. Choose from multiple question types.'
                  }
                </p>
                <button style={s.btnPrimary} onClick={() => setShowNewForm(true)}>
                  + Create First Form
                </button>
              </>
            )}
          </div>
        ) : (
          <div style={s.formsGrid}>
            {displayForms.map(form => (
              <div key={form._id} style={s.formCard}>
                <div style={s.formCardHeader}>
                  <h3 style={s.formName}>{form.name}</h3>
                  {getStatusBadge(form.status)}
                </div>
                <p style={s.formDesc}>{form.description || 'No description'}</p>

                {/* Stat strip. Replaces the old emoji meta row: the response
                    count is the thing Steve actually looks for, so it reads as
                    a number rather than being buried in a button label. */}
                <div style={s.formStats}>
                  <div style={s.stat}>
                    <span style={{ ...s.statValue, ...(form.totalSubmissions > 0 ? s.statValueActive : {}) }}>
                      {form.totalSubmissions || 0}
                    </span>
                    <span style={s.statLabel}>{form.totalSubmissions === 1 ? 'Response' : 'Responses'}</span>
                  </div>
                  <div style={s.stat}>
                    <span style={s.statValue}>{audienceLabel(form.targetAudience)}</span>
                    <span style={s.statLabel}>Open to</span>
                  </div>
                  {form.formType !== 'feedback' && (
                    <div style={s.stat}>
                      <span style={s.statValue}>${form.reportConfig?.price || 149}</span>
                      <span style={s.statLabel}>Price</span>
                    </div>
                  )}
                </div>

                <div style={s.formActions}>
                  {listView === 'forms' && (
                    <>
                      {/* Primary row: the three things done most often. */}
                      <div style={s.actionRow}>
                        <button style={s.btnEdit} onClick={() => handleOpenEditor(form)}>Edit form</button>
                        <button
                          style={s.btnShare2}
                          onClick={() => {
                            const link = `${window.location.origin}/f/${form.shareToken}`;
                            copyToClipboard(link,
                              () => { setMessage({ type: 'success', text: 'Form link copied!' }); setTimeout(() => setMessage({ type: '', text: '' }), 2000); },
                              () => { setMessage({ type: 'error', text: 'Could not copy. Please copy manually.' }); }
                            );
                          }}
                        >
                          Copy link
                        </button>
                        {form.formType === 'feedback' && (
                          <button
                            style={{ ...s.btnResponses, ...(form.totalSubmissions ? {} : s.btnMuted) }}
                            disabled={!form.totalSubmissions}
                            title={form.totalSubmissions ? 'View submitted responses' : 'No responses yet'}
                            onClick={() => navigate(`/admin/forms/${form._id}/responses`)}
                          >
                            View responses
                          </button>
                        )}
                      </div>
                      {/* Secondary row. Archive is destructive, so it sits apart
                          and quiet rather than competing with Edit. */}
                      <div style={s.actionRowQuiet}>
                        <button style={s.btnQuiet} onClick={() => handleDuplicate(form._id)}>Duplicate</button>
                        <button style={s.btnQuietDanger} onClick={() => handleArchive(form._id)}>Archive</button>
                      </div>
                    </>
                  )}
                  {listView === 'archived' && (
                    <div style={s.actionRow}>
                      <button style={s.btnRestore} onClick={() => handleRestore(form._id)}>↩ Restore</button>
                      <button style={s.btnArchive} onClick={() => handleTrash(form._id)}>Move to trash</button>
                    </div>
                  )}
                  {listView === 'trash' && (
                    <div style={s.actionRow}>
                      <button style={s.btnRestore} onClick={() => handleRestore(form._id)}>↩ Restore</button>
                      <button
                        style={{ ...s.btnDeleteForever, ...(form.totalSubmissions > 0 ? s.btnDisabled : {}) }}
                        title={form.totalSubmissions > 0 ? 'Blocked: this form has responses linked to it' : 'Permanently delete this form'}
                        onClick={() => handlePermanentDelete(form)}
                      >
                        Delete forever
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// FORM EDITOR COMPONENT
// ─────────────────────────────────────────

const FormEditor = ({ form: initialForm, onBack, navigate, logout }) => {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Assessment editor state
  const [newDimension, setNewDimension] = useState({ name: '', leftPoleName: '', rightPoleName: '' });
  const [newQuestion, setNewQuestion] = useState({ questionText: '', questionType: 'likert', dimensionId: '', pole: 'left', reverseScored: false });

  // Feedback editor state
  const [editingQuestion, setEditingQuestion] = useState(null); // question index being edited
  const [newFeedbackQ, setNewFeedbackQ] = useState({
    questionText: '', questionType: 'multiple_choice', options: ['', ''], required: true, helpText: '',
    minLabel: '', maxLabel: '', minValue: 1, maxValue: 5,
  });

  const isAssessment = form.formType !== 'feedback';

  const refreshForm = async () => {
    try {
      const response = await axios.get(`/forms/${form._id}`);
      if (response.data.success) setForm(response.data.data.form);
    } catch (err) { console.error(err); }
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // Assessment: Add dimension
  const handleAddDimension = async () => {
    if (!newDimension.name || !newDimension.leftPoleName || !newDimension.rightPoleName) {
      showMsg('error', 'All dimension fields are required'); return;
    }
    try {
      const response = await axios.post(`/forms/${form._id}/dimensions`, newDimension);
      if (response.data.success) {
        setForm(prev => ({ ...prev, dimensions: response.data.data.dimensions, orgMappingQuestions: response.data.data.orgMappingQuestions }));
        setNewDimension({ name: '', leftPoleName: '', rightPoleName: '' });
        showMsg('success', 'Dimension added');
      }
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to add dimension');
    }
  };

  const handleDeleteDimension = async (dimensionId) => {
    if (!window.confirm('Delete this dimension?')) return;
    try {
      const response = await axios.delete(`/forms/${form._id}/dimensions/${dimensionId}`);
      setForm(prev => ({ ...prev, dimensions: response.data.data.dimensions }));
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Cannot delete. Remove questions first.');
    }
  };

  // Assessment: Add question
  const handleAddAssessmentQuestion = async () => {
    if (!newQuestion.questionText || !newQuestion.dimensionId) {
      showMsg('error', 'Question text and dimension are required'); return;
    }
    try {
      const response = await axios.post(`/forms/${form._id}/questions`, newQuestion);
      if (response.data.success) {
        setForm(prev => ({ ...prev, questions: response.data.data.questions }));
        setNewQuestion({ questionText: '', questionType: 'likert', dimensionId: '', pole: 'left', reverseScored: false });
        showMsg('success', 'Question added');
      }
    } catch (err) {
      showMsg('error', 'Failed to add question');
    }
  };

  // Feedback: Add question
  const handleAddFeedbackQuestion = async () => {
    if (!newFeedbackQ.questionText.trim()) {
      showMsg('error', 'Question text is required'); return;
    }

    const needsOptions = ['multiple_choice', 'checkbox', 'dropdown', 'ranking'].includes(newFeedbackQ.questionType);
    if (needsOptions && newFeedbackQ.options.filter(o => o.trim()).length < 2) {
      showMsg('error', 'At least 2 options are required'); return;
    }

    try {
      const qData = {
        questionText: newFeedbackQ.questionText.trim(),
        questionType: newFeedbackQ.questionType,
        required: newFeedbackQ.required,
        helpText: newFeedbackQ.helpText,
        options: needsOptions ? newFeedbackQ.options.filter(o => o.trim()) : [],
        minLabel: newFeedbackQ.minLabel,
        maxLabel: newFeedbackQ.maxLabel,
        minValue: newFeedbackQ.minValue,
        maxValue: newFeedbackQ.maxValue,
      };

      const response = await axios.post(`/forms/${form._id}/questions`, qData);
      if (response.data.success) {
        setForm(prev => ({ ...prev, questions: response.data.data.questions }));
        setNewFeedbackQ({ questionText: '', questionType: 'multiple_choice', options: ['', ''], required: true, helpText: '', minLabel: '', maxLabel: '', minValue: 1, maxValue: 5 });
        showMsg('success', 'Question added');
      }
    } catch (err) {
      showMsg('error', 'Failed to add question');
    }
  };

  // Move question up or down
  const handleMoveQuestion = async (index, direction) => {
    const questions = [...form.questions].sort((a, b) => a.order - b.order);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;

    // Swap orders
    [questions[index].order, questions[newIndex].order] = [questions[newIndex].order, questions[index].order];
    setForm(prev => ({ ...prev, questions }));

    try {
      const orderedIds = questions.sort((a, b) => a.order - b.order).map(q => q.questionNumber);
      await axios.put(`/forms/${form._id}/questions/reorder`, { orderedIds });
    } catch (err) {
      console.error('Reorder failed:', err);
    }
  };

  const handleToggleQuestion = async (questionNumber, active) => {
    try {
      await axios.put(`/forms/${form._id}/questions/${questionNumber}`, { active });
      setForm(prev => ({ ...prev, questions: prev.questions.map(q => q.questionNumber === questionNumber ? { ...q, active } : q) }));
    } catch (err) { console.error(err); }
  };

  const handleDeleteQuestion = async (questionNumber) => {
    if (!window.confirm('Delete this question?\n\nIt will be removed from the form and remaining questions will be renumbered. This cannot be undone.')) return;
    try {
      await axios.delete(`/forms/${form._id}/questions/${questionNumber}`);
      await refreshForm();
      showMsg('success', 'Question deleted');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to delete question');
    }
  };

  const handlePublish = async (status) => {
    setSaving(true);
    try {
      await axios.put(`/forms/${form._id}`, { status });
      showMsg('success', status === 'active' ? 'Form published!' : 'Saved as draft');
      setTimeout(onBack, 1500);
    } catch (err) {
      showMsg('error', 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sortedQuestions = [...(form.questions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div style={s.page}>
      <Sidebar navigate={navigate} logout={logout} />
      <div style={s.main}>
        <button style={s.backBtn} onClick={onBack}>← Back to Forms</button>

        <div style={s.editorHeader}>
          <div>
            <h1 style={s.title}>{form.name}</h1>
            <p style={s.subtitle}>
              {isAssessment ? '📋 Assessment Form' : '💬 Feedback Form'} · {form.status}
            </p>
          </div>
          <div style={s.editorActions}>
            <button style={s.btnDraft} onClick={() => handlePublish('draft')} disabled={saving}>
              Save as Draft
            </button>
            <button style={s.btnPublish} onClick={() => handlePublish('active')} disabled={saving}>
              {saving ? 'Saving...' : '✅ Publish'}
            </button>
          </div>
        </div>

        {message.text && (
          <div style={{ ...s.messageBox, ...(message.type === 'success' ? s.messageSuccess : s.messageError) }}>
            {message.text}
          </div>
        )}

        {/* ASSESSMENT EDITOR */}
        {isAssessment && (
          <>
            {/* Dimensions */}
            <div style={s.section}>
              <h2 style={s.sectionTitle}>Dimensions</h2>
              <p style={s.sectionDesc}>Each dimension has two opposite poles. Add questions for each pole below.</p>

              {form.dimensions?.map(dim => (
                <div key={dim.dimensionId} style={s.dimCard}>
                  <div style={s.dimCardHeader}>
                    <div>
                      <span style={s.dimCardName}>{dim.name}</span>
                      <span style={s.dimCardPoles}>{dim.leftPoleName} ↔ {dim.rightPoleName}</span>
                    </div>
                    <button style={s.btnDeleteSmall} onClick={() => handleDeleteDimension(dim.dimensionId)}>Remove</button>
                  </div>
                </div>
              ))}

              <div style={s.addBox}>
                <div style={s.threeCol}>
                  <div>
                    <label style={s.label}>Dimension Name *</label>
                    <input style={s.input} placeholder="Goal Orientation" value={newDimension.name} onChange={e => setNewDimension(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div>
                    <label style={s.label}>Left Pole *</label>
                    <input style={s.input} placeholder="Intentional" value={newDimension.leftPoleName} onChange={e => setNewDimension(p => ({ ...p, leftPoleName: e.target.value }))} />
                  </div>
                  <div>
                    <label style={s.label}>Right Pole *</label>
                    <input style={s.input} placeholder="Adapting" value={newDimension.rightPoleName} onChange={e => setNewDimension(p => ({ ...p, rightPoleName: e.target.value }))} />
                  </div>
                </div>
                <button style={s.btnAdd} onClick={handleAddDimension}>+ Add Dimension</button>
              </div>
            </div>

            {/* Questions grouped by dimension */}
            {form.dimensions?.length > 0 && (
              <div style={s.section}>
                <h2 style={s.sectionTitle}>Questions</h2>
                {form.dimensions.map(dim => {
                  const leftQ = sortedQuestions.filter(q => q.dimensionId === dim.dimensionId && q.pole === 'left');
                  const rightQ = sortedQuestions.filter(q => q.dimensionId === dim.dimensionId && q.pole === 'right');
                  return (
                    <div key={dim.dimensionId} style={s.dimQSection}>
                      <h3 style={s.dimQTitle}>{dim.name}</h3>
                      <div style={s.twoPoles}>
                        {[{ label: dim.leftPoleName, pole: 'left', questions: leftQ }, { label: dim.rightPoleName, pole: 'right', questions: rightQ }].map(({ label, pole, questions }) => (
                          <div key={pole} style={s.poleSection}>
                            <div style={s.poleSectionTitle}>{label} ({questions.length} questions)</div>
                            {questions.map((q, idx) => (
                              <div key={q._id} style={{ ...s.questionCard, ...(q.active ? {} : s.questionCardInactive) }}>
                                <div style={s.questionCardLeft}>
                                  <span style={s.qNum}>Q{q.questionNumber}</span>
                                  <span style={s.qText}>{q.questionText}</span>
                                </div>
                                <div style={s.questionCardRight}>
                                  <button
                                    style={s.btnToggle}
                                    title="Edit question text"
                                    onClick={() => {
                                      const newText = window.prompt('Edit question text:', q.questionText);
                                      if (newText && newText.trim() && newText !== q.questionText) {
                                        axios.put(`/forms/${form._id}/questions/${q.questionNumber}`, { questionText: newText.trim() })
                                          .then(() => { refreshForm(); showMsg('success', 'Question updated'); })
                                          .catch(() => showMsg('error', 'Failed to update'));
                                      }
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button style={s.btnToggle} onClick={() => handleToggleQuestion(q.questionNumber, !q.active)}>
                                    {q.active ? 'Hide' : 'Show'}
                                  </button>
                                  <button
                                    style={s.btnDeleteQ}
                                    title="Delete this question"
                                    onClick={() => handleDeleteQuestion(q.questionNumber)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div style={s.addBox}>
                  <h3 style={s.addBoxTitle}>Add Question</h3>
                  <div style={s.formGroup}>
                    <label style={s.label}>Question Text *</label>
                    <textarea style={s.textarea} rows={2} placeholder="e.g. I prefer to plan ahead carefully" value={newQuestion.questionText} onChange={e => setNewQuestion(p => ({ ...p, questionText: e.target.value }))} />
                  </div>
                  <div style={s.threeCol}>
                    <div>
                      <label style={s.label}>Dimension *</label>
                      <select style={s.select} value={newQuestion.dimensionId} onChange={e => setNewQuestion(p => ({ ...p, dimensionId: e.target.value }))}>
                        <option value="">Select...</option>
                        {form.dimensions?.map(d => <option key={d.dimensionId} value={d.dimensionId}>{d.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={s.label}>Pole *</label>
                      <select style={s.select} value={newQuestion.pole} onChange={e => setNewQuestion(p => ({ ...p, pole: e.target.value }))}>
                        <option value="left">Left Pole</option>
                        <option value="right">Right Pole</option>
                      </select>
                    </div>
                    <div>
                      <label style={s.label}>Type</label>
                      <select style={s.select} value={newQuestion.questionType} onChange={e => setNewQuestion(p => ({ ...p, questionType: e.target.value }))}>
                        <option value="likert">Likert (0-4)</option>
                        <option value="yes_no">Yes / No</option>
                      </select>
                    </div>
                  </div>
                  <label style={{ ...s.label, display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer', marginBottom: '14px' }}>
                    <input type="checkbox" checked={newQuestion.reverseScored} onChange={e => setNewQuestion(p => ({ ...p, reverseScored: e.target.checked }))} />
                    Reverse scored
                  </label>
                  <button style={s.btnAdd} onClick={handleAddAssessmentQuestion}>+ Add Question</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* FEEDBACK EDITOR */}
        {!isAssessment && (
          <div style={s.section}>
            <h2 style={s.sectionTitle}>Questions ({sortedQuestions.length})</h2>
            <p style={s.sectionDesc}>Use arrows to reorder. Click Edit to change a question.</p>

            {sortedQuestions.map((q, idx) => {
              const typeInfo = FEEDBACK_QUESTION_TYPES.find(t => t.value === q.questionType) || { icon: '❓', label: q.questionType };
              const isEditing = editingQuestion === idx;

              return (
                <div key={q._id || idx} style={{ ...s.feedbackQCard, ...(q.active ? {} : s.feedbackQCardInactive) }}>
                  <div style={s.feedbackQTop}>
                    <div style={s.feedbackQOrder}>
                      <button style={s.btnArrow} onClick={() => handleMoveQuestion(idx, 'up')} disabled={idx === 0}>↑</button>
                      <button style={s.btnArrow} onClick={() => handleMoveQuestion(idx, 'down')} disabled={idx === sortedQuestions.length - 1}>↓</button>
                    </div>
                    <div style={s.feedbackQContent}>
                      <div style={s.feedbackQHeader}>
                        <span style={s.qTypeIcon}>{typeInfo.icon}</span>
                        <span style={s.qTypeName}>{typeInfo.label}</span>
                        {q.required && <span style={s.requiredBadge}>Required</span>}
                        {!q.active && <span style={s.inactiveBadge}>Hidden</span>}
                      </div>
                      <div style={s.feedbackQText}>{q.questionText}</div>
                      {q.options?.length > 0 && (
                        <div style={s.qOptions}>
                          {q.options.map((opt, i) => <span key={i} style={s.qOption}>{opt}</span>)}
                        </div>
                      )}
                    </div>
                    <div style={s.feedbackQActions}>
                      <button
                        style={{ ...s.btnArrow, color: isEditing ? 'var(--color-teal)' : 'var(--color-text)' }}
                        onClick={() => setEditingQuestion(isEditing ? null : idx)}
                      >
                        {isEditing ? 'Close' : 'Edit'}
                      </button>
                      <button style={s.btnToggle} onClick={() => handleToggleQuestion(q.questionNumber, !q.active)}>
                        {q.active ? 'Hide' : 'Show'}
                      </button>
                      <button
                        style={s.btnDeleteQ}
                        title="Delete this question"
                        onClick={() => handleDeleteQuestion(q.questionNumber)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Inline edit form */}
                  {isEditing && (
                    <div style={s.inlineEditForm}>
                      <div style={s.formGroup}>
                        <label style={s.label}>Question Text</label>
                        <textarea
                          style={s.textarea}
                          rows={2}
                          defaultValue={q.questionText}
                          id={`edit_text_${q.questionNumber}`}
                        />
                      </div>
                      {['multiple_choice', 'checkbox', 'dropdown', 'ranking'].includes(q.questionType) && (
                        <div style={s.formGroup}>
                          <label style={s.label}>Options</label>
                          {(q.options || []).map((opt, i) => (
                            <input
                              key={i}
                              style={{ ...s.input, marginBottom: '6px' }}
                              defaultValue={opt}
                              id={`edit_opt_${q.questionNumber}_${i}`}
                            />
                          ))}
                        </div>
                      )}
                      {['rating', 'nps', 'bipolar'].includes(q.questionType) && (
                        <div style={s.twoCol}>
                          <div style={s.formGroup}>
                            <label style={s.label}>Min Label</label>
                            <input style={s.input} defaultValue={q.minLabel || ''} id={`edit_minlabel_${q.questionNumber}`} />
                          </div>
                          <div style={s.formGroup}>
                            <label style={s.label}>Max Label</label>
                            <input style={s.input} defaultValue={q.maxLabel || ''} id={`edit_maxlabel_${q.questionNumber}`} />
                          </div>
                        </div>
                      )}
                      <div style={s.twoCol}>
                        <label style={{ ...s.label, display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
                          <input type="checkbox" defaultChecked={q.required} id={`edit_req_${q.questionNumber}`} />
                          Required
                        </label>
                      </div>
                      <button
                        style={s.btnAdd}
                        onClick={async () => {
                          const updates = {
                            questionText: document.getElementById(`edit_text_${q.questionNumber}`)?.value,
                            required: document.getElementById(`edit_req_${q.questionNumber}`)?.checked,
                          };
                          if (['multiple_choice', 'checkbox', 'dropdown', 'ranking'].includes(q.questionType)) {
                            const opts = [];
                            (q.options || []).forEach((_, i) => {
                              const val = document.getElementById(`edit_opt_${q.questionNumber}_${i}`)?.value;
                              if (val) opts.push(val);
                            });
                            updates.options = opts;
                          }
                          if (['rating', 'nps', 'bipolar'].includes(q.questionType)) {
                            updates.minLabel = document.getElementById(`edit_minlabel_${q.questionNumber}`)?.value;
                            updates.maxLabel = document.getElementById(`edit_maxlabel_${q.questionNumber}`)?.value;
                          }
                          try {
                            await axios.put(`/forms/${form._id}/questions/${q.questionNumber}`, updates);
                            setEditingQuestion(null);
                            refreshForm();
                            showMsg('success', 'Question updated');
                          } catch (err) {
                            showMsg('error', 'Failed to update question');
                          }
                        }}
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add new feedback question */}
            <div style={s.addBox}>
              <h3 style={s.addBoxTitle}>Add Question</h3>

              {/* Question type selector */}
              <div style={s.formGroup}>
                <label style={s.label}>Question Type *</label>
                <div style={s.qTypeGrid}>
                  {FEEDBACK_QUESTION_TYPES.map(t => (
                    <button
                      key={t.value}
                      type="button"
                      style={{ ...s.qTypeBtn, ...(newFeedbackQ.questionType === t.value ? s.qTypeBtnActive : {}) }}
                      onClick={() => setNewFeedbackQ(p => ({ ...p, questionType: t.value }))}
                    >
                      <span style={s.qTypeBtnIcon}>{t.icon}</span>
                      <span style={s.qTypeBtnLabel}>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Question Text *</label>
                <textarea style={s.textarea} rows={2} placeholder="Enter your question..." value={newFeedbackQ.questionText} onChange={e => setNewFeedbackQ(p => ({ ...p, questionText: e.target.value }))} />
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Help Text <span style={{ fontWeight: '400', color: 'var(--color-text-light)' }}>(optional)</span></label>
                <input style={s.input} placeholder="Additional instructions for the respondent..." value={newFeedbackQ.helpText} onChange={e => setNewFeedbackQ(p => ({ ...p, helpText: e.target.value }))} />
              </div>

              {/* Options for multiple choice, checkbox, dropdown, ranking */}
              {['multiple_choice', 'checkbox', 'dropdown', 'ranking'].includes(newFeedbackQ.questionType) && (
                <div style={s.formGroup}>
                  <label style={s.label}>Options *</label>
                  {newFeedbackQ.options.map((opt, i) => (
                    <div key={i} style={s.optionRow}>
                      <input
                        style={{ ...s.input, flex: 1 }}
                        placeholder={`Option ${i + 1}`}
                        value={opt}
                        onChange={e => {
                          const newOpts = [...newFeedbackQ.options];
                          newOpts[i] = e.target.value;
                          setNewFeedbackQ(p => ({ ...p, options: newOpts }));
                        }}
                      />
                      {newFeedbackQ.options.length > 2 && (
                        <button
                          type="button"
                          style={s.btnRemoveOption}
                          onClick={() => setNewFeedbackQ(p => ({ ...p, options: p.options.filter((_, idx) => idx !== i) }))}
                        >✕</button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    style={s.btnAddOption}
                    onClick={() => setNewFeedbackQ(p => ({ ...p, options: [...p.options, ''] }))}
                  >
                    + Add Option
                  </button>
                </div>
              )}

              {/* Labels for rating, nps, bipolar */}
              {['rating', 'nps', 'bipolar'].includes(newFeedbackQ.questionType) && (
                <div style={s.twoCol}>
                  <div style={s.formGroup}>
                    <label style={s.label}>Min Label</label>
                    <input style={s.input} placeholder="Not at all likely" value={newFeedbackQ.minLabel} onChange={e => setNewFeedbackQ(p => ({ ...p, minLabel: e.target.value }))} />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Max Label</label>
                    <input style={s.input} placeholder="Extremely likely" value={newFeedbackQ.maxLabel} onChange={e => setNewFeedbackQ(p => ({ ...p, maxLabel: e.target.value }))} />
                  </div>
                  {newFeedbackQ.questionType === 'rating' && (
                    <>
                      <div style={s.formGroup}>
                        <label style={s.label}>Min Value</label>
                        <input style={s.input} type="number" value={newFeedbackQ.minValue} onChange={e => setNewFeedbackQ(p => ({ ...p, minValue: parseInt(e.target.value) }))} min="0" max="5" />
                      </div>
                      <div style={s.formGroup}>
                        <label style={s.label}>Max Value</label>
                        <input style={s.input} type="number" value={newFeedbackQ.maxValue} onChange={e => setNewFeedbackQ(p => ({ ...p, maxValue: parseInt(e.target.value) }))} min="2" max="10" />
                      </div>
                    </>
                  )}
                </div>
              )}

              <div style={s.twoCol}>
                <label style={{ ...s.label, display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={newFeedbackQ.required} onChange={e => setNewFeedbackQ(p => ({ ...p, required: e.target.checked }))} />
                  Required question
                </label>
              </div>

              <button style={{ ...s.btnAdd, marginTop: '12px' }} onClick={handleAddFeedbackQuestion}>+ Add Question</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: 'var(--color-text-light)' },
  btnPrimary: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  messageBox: { padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  messageSuccess: { backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)', border: '1px solid #A9DFBF' },
  messageError: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid #F5C6CB' },
  tabs: { display: 'flex', gap: '4px', backgroundColor: '#fff', padding: '4px', borderRadius: '10px', border: '1px solid var(--color-border)', marginBottom: '0', width: 'fit-content' },
  tab: { padding: '9px 20px', borderRadius: '7px', border: 'none', backgroundColor: 'transparent', color: 'var(--color-text-light)', fontSize: '14px', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' },
  tabActive: { backgroundColor: 'var(--color-primary)', color: '#fff' },
  tabDesc: { backgroundColor: 'var(--color-info-bg)', borderRadius: '8px', padding: '12px 16px', marginTop: '8px', marginBottom: '20px' },
  viewPills: { display: 'flex', gap: '8px', marginBottom: '16px' },
  viewPill: { backgroundColor: '#fff', color: 'var(--color-text-light)', padding: '8px 16px', borderRadius: '100px', border: '1px solid var(--color-border)', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  viewPillActive: { backgroundColor: 'var(--color-primary)', color: '#fff', border: '1px solid var(--color-primary)' },
  viewNote: { backgroundColor: '#FEF9E7', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', fontSize: '13px', color: '#7D6608' },
  btnRestore: { backgroundColor: '#E8F8EF', color: '#1E8449', padding: '8px 16px', borderRadius: '8px', border: '1px solid #1E8449', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnDeleteForever: { backgroundColor: '#922B21', color: '#fff', padding: '8px 16px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  btnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  loadingText: { padding: '40px', textAlign: 'center', color: 'var(--color-text-light)' },
  empty: { padding: '60px 20px', textAlign: 'center', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: 'var(--color-text-light)', maxWidth: '420px', margin: '0 auto 20px', lineHeight: '1.6' },
  // Cards were 300px minimum with a five button non-wrapping flex row, so the
  // last button overflowed and was clipped by the card edge. Wider minimum,
  // wrapping rows, and the actions split by importance.
  formsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px', alignItems: 'stretch' },
  formCard: { display: 'flex', flexDirection: 'column', backgroundColor: '#fff', borderRadius: '14px', border: '1px solid var(--color-border)', padding: '22px', boxShadow: 'var(--shadow-sm)', height: '100%' },
  formCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '6px' },
  formName: { fontSize: '16px', fontWeight: '650', color: 'var(--color-primary)', lineHeight: '1.35', minWidth: 0 },
  formDesc: { fontSize: '13px', color: 'var(--color-text-light)', lineHeight: '1.55', marginBottom: '18px' },

  // Stat strip
  formStats: { display: 'flex', gap: '28px', flexWrap: 'wrap', padding: '14px 0', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', marginBottom: '18px' },
  stat: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  statValue: { fontSize: '17px', fontWeight: '650', color: 'var(--color-text-light)', lineHeight: '1.2' },
  statValueActive: { color: 'var(--color-teal)' },
  statLabel: { fontSize: '11px', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: '500' },

  // Actions. marginTop auto pins them to the bottom so cards of differing
  // description length still line their buttons up across a row.
  formActions: { marginTop: 'auto' },
  actionRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  actionRowQuiet: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--color-border)' },

  btnEdit: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--color-teal)', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  btnShare2: { backgroundColor: '#fff', color: 'var(--color-primary)', padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnResponses: { backgroundColor: '#fff', color: 'var(--color-primary)', padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnMuted: { color: 'var(--color-text-light)', borderColor: 'var(--color-border)', backgroundColor: 'var(--color-off-white)', cursor: 'not-allowed' },
  btnQuiet: { background: 'none', border: 'none', padding: 0, color: 'var(--color-text-light)', fontSize: '12.5px', fontWeight: '500', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' },
  btnQuietDanger: { background: 'none', border: 'none', padding: 0, color: 'var(--color-error)', fontSize: '12.5px', fontWeight: '500', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' },
  btnDuplicate: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '6px 14px', borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '12px', cursor: 'pointer' },
  btnArchive: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--color-error)', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },

  // Modal
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '16px', padding: '32px', width: '560px', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '20px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '20px' },
  modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },
  btnCancel: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '14px', cursor: 'pointer' },
  btnCreate: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '10px 24px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  typeSelector: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' },
  typeOption: { backgroundColor: '#fff', border: '2px solid var(--color-border)', borderRadius: '10px', padding: '16px', cursor: 'pointer', textAlign: 'left' },
  typeOptionActive: { border: '2px solid var(--color-teal)', backgroundColor: 'var(--color-info-bg)' },
  typeOptionIcon: { fontSize: '24px', marginBottom: '6px' },
  typeOptionLabel: { fontSize: '14px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '4px' },
  typeOptionDesc: { fontSize: '12px', color: 'var(--color-text-light)', lineHeight: '1.4' },

  // Editor
  backBtn: { background: 'none', border: 'none', color: 'var(--color-teal)', fontSize: '14px', cursor: 'pointer', marginBottom: '16px', fontWeight: '500', padding: 0 },
  editorHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  editorActions: { display: 'flex', gap: '10px' },
  btnDraft: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '14px', cursor: 'pointer' },
  btnPublish: { backgroundColor: '#27AE60', color: '#fff', padding: '10px 24px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  section: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '24px', marginBottom: '20px', boxShadow: 'var(--shadow-sm)' },
  sectionTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '6px' },
  sectionDesc: { fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '20px' },
  dimCard: { backgroundColor: 'var(--color-off-white)', borderRadius: '8px', padding: '12px 16px', marginBottom: '8px' },
  dimCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dimCardName: { fontSize: '14px', fontWeight: '600', color: 'var(--color-primary)', marginRight: '12px' },
  dimCardPoles: { fontSize: '12px', color: 'var(--color-text-light)' },
  btnDeleteSmall: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', padding: '4px 10px', borderRadius: '5px', border: '1px solid var(--color-error)', fontSize: '11px', cursor: 'pointer' },
  addBox: { backgroundColor: 'var(--color-off-white)', borderRadius: '10px', padding: '20px', marginTop: '16px' },
  addBoxTitle: { fontSize: '14px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '16px' },
  threeCol: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  formGroup: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--color-text)', marginBottom: '6px' },
  input: { width: '100%', padding: '9px 12px', border: '1.5px solid var(--color-border)', borderRadius: '7px', fontSize: '13px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '9px 12px', border: '1.5px solid var(--color-border)', borderRadius: '7px', fontSize: '13px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box', resize: 'vertical' },
  select: { width: '100%', padding: '9px 12px', border: '1.5px solid var(--color-border)', borderRadius: '7px', fontSize: '13px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fff' },
  btnAdd: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  dimQSection: { marginBottom: '24px' },
  dimQTitle: { fontSize: '15px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '12px', borderBottom: '1px solid var(--color-border)', paddingBottom: '8px' },
  twoPoles: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '8px' },
  poleSection: {},
  poleSectionTitle: { fontSize: '12px', fontWeight: '600', color: 'var(--color-teal)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  questionCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff', borderRadius: '6px', padding: '8px 10px', marginBottom: '4px', border: '1px solid var(--color-border)', gap: '8px' },
  questionCardInactive: { opacity: 0.4 },
  questionCardLeft: { display: 'flex', gap: '8px', alignItems: 'flex-start', flex: 1 },
  questionCardRight: {},
  qNum: { fontSize: '10px', fontWeight: '700', color: 'var(--color-teal)', flexShrink: 0, paddingTop: '2px' },
  qText: { fontSize: '12px', color: 'var(--color-text)', lineHeight: '1.4' },
  btnToggle: { backgroundColor: '#F4F6F9', border: '1px solid var(--color-border)', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '500', padding: '3px 9px', color: 'var(--color-text)' },
  btnDeleteQ: { backgroundColor: '#FDEDEC', border: '1px solid #E74C3C', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '500', padding: '3px 9px', color: '#943126' },

  // Feedback editor
  feedbackQCard: { backgroundColor: '#fff', borderRadius: '10px', border: '1px solid var(--color-border)', padding: '16px', marginBottom: '8px', boxShadow: 'var(--shadow-sm)' },
  feedbackQCardInactive: { opacity: 0.5, backgroundColor: 'var(--color-off-white)' },
  inlineEditForm: { backgroundColor: 'var(--color-off-white)', borderRadius: '8px', padding: '16px', marginTop: '12px', borderTop: '1px solid var(--color-border)' },
  feedbackQTop: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  feedbackQOrder: { display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 },
  btnArrow: { backgroundColor: 'var(--color-off-white)', border: '1px solid var(--color-border)', borderRadius: '4px', width: '26px', height: '26px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  feedbackQContent: { flex: 1 },
  feedbackQHeader: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' },
  feedbackQActions: { flexShrink: 0 },
  feedbackQText: { fontSize: '14px', fontWeight: '500', color: 'var(--color-primary)', lineHeight: '1.5' },
  qTypeIcon: { fontSize: '14px' },
  qTypeName: { fontSize: '12px', color: 'var(--color-text-light)', fontWeight: '500' },
  requiredBadge: { fontSize: '10px', backgroundColor: '#EBF5FB', color: '#1A5276', padding: '2px 8px', borderRadius: '100px', fontWeight: '500' },
  inactiveBadge: { fontSize: '10px', backgroundColor: '#F4F6F9', color: '#7F8C8D', padding: '2px 8px', borderRadius: '100px', fontWeight: '500' },
  qOptions: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' },
  qOption: { fontSize: '11px', backgroundColor: 'var(--color-off-white)', border: '1px solid var(--color-border)', padding: '3px 8px', borderRadius: '4px', color: 'var(--color-text-light)' },
  qHelpText: { fontSize: '11px', color: 'var(--color-text-light)', marginTop: '4px', fontStyle: 'italic' },
  qTypeGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '8px' },
  qTypeBtn: { backgroundColor: '#fff', border: '1.5px solid var(--color-border)', borderRadius: '8px', padding: '8px 6px', cursor: 'pointer', textAlign: 'center' },
  qTypeBtnActive: { border: '1.5px solid var(--color-teal)', backgroundColor: 'var(--color-info-bg)' },
  qTypeBtnIcon: { display: 'block', fontSize: '18px', marginBottom: '4px' },
  qTypeBtnLabel: { display: 'block', fontSize: '10px', color: 'var(--color-text)', fontWeight: '500' },
  optionRow: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' },
  btnRemoveOption: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: 'none', borderRadius: '4px', width: '28px', height: '28px', cursor: 'pointer', flexShrink: 0, fontSize: '12px' },
  btnAddOption: { background: 'none', border: 'none', color: 'var(--color-teal)', fontSize: '13px', cursor: 'pointer', fontWeight: '500', padding: '4px 0' },
};

export default FormMaker;
